import { Capacitor } from '@capacitor/core';

export interface DeferredDeepLinkData {
  uid?: string;
  source?: 'install_referrer' | 'intent' | 'local_storage';
  timestamp?: number;
}

class DeferredDeepLinkService {
  private static instance: DeferredDeepLinkService;
  private isInitialized = false;
  private deferredData: DeferredDeepLinkData | null = null;

  private constructor() {}

  static getInstance(): DeferredDeepLinkService {
    if (!DeferredDeepLinkService.instance) {
      DeferredDeepLinkService.instance = new DeferredDeepLinkService();
    }
    return DeferredDeepLinkService.instance;
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    console.log('Initializing deferred deep link service...');

    if (Capacitor.isNativePlatform()) {
      await this.handleAndroidInstallReferrer();
    }

    // Check for any stored deferred data
    this.loadStoredDeferredData();

    this.isInitialized = true;
    console.log('Deferred deep link service initialized');
  }

  private async handleAndroidInstallReferrer(): Promise<void> {
    try {
      // For Android, we need to check if this is the first launch after installation
      const isFirstLaunch = await this.isFirstLaunchAfterInstall();
      
      if (isFirstLaunch) {
        console.log('First launch after install detected');
        
        // Try to get install referrer data
        const referrerData = await this.getInstallReferrerData();
        if (referrerData) {
          console.log('Install referrer data found:', referrerData);
          this.deferredData = {
            uid: referrerData.uid,
            source: 'install_referrer',
            timestamp: Date.now()
          };
          
          // Store for later use
          this.storeDeferredData();
        }
      }
    } catch (error) {
      console.warn('Failed to handle Android install referrer:', error);
    }
  }

  private async isFirstLaunchAfterInstall(): Promise<boolean> {
    try {
      const hasLaunchedBefore = localStorage.getItem('app_has_launched_before');
      if (!hasLaunchedBefore) {
        localStorage.setItem('app_has_launched_before', 'true');
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Failed to check first launch status:', error);
      return false;
    }
  }

  private async getInstallReferrerData(): Promise<{ uid: string } | null> {
    try {
      // This would typically use the Google Play Install Referrer API
      // For now, we'll check if there's any stored referrer data
      const referrerData = localStorage.getItem('install_referrer_data');
      if (referrerData) {
        const parsed = JSON.parse(referrerData);
        return parsed;
      }
      return null;
    } catch (error) {
      console.warn('Failed to get install referrer data:', error);
      return null;
    }
  }

  private loadStoredDeferredData(): void {
    try {
      const stored = localStorage.getItem('deferred_deep_link_data');
      if (stored) {
        this.deferredData = JSON.parse(stored);
        console.log('Loaded stored deferred data:', this.deferredData);
      }
    } catch (error) {
      console.warn('Failed to load stored deferred data:', error);
    }
  }

  private storeDeferredData(): void {
    if (this.deferredData) {
      try {
        localStorage.setItem('deferred_deep_link_data', JSON.stringify(this.deferredData));
        console.log('Stored deferred data:', this.deferredData);
      } catch (error) {
        console.warn('Failed to store deferred data:', error);
      }
    }
  }

  setDeferredData(data: DeferredDeepLinkData): void {
    this.deferredData = data;
    this.storeDeferredData();
  }

  getDeferredData(): DeferredDeepLinkData | null {
    return this.deferredData;
  }

  clearDeferredData(): void {
    this.deferredData = null;
    try {
      localStorage.removeItem('deferred_deep_link_data');
      localStorage.removeItem('install_referrer_data');
      console.log('Cleared deferred data');
    } catch (error) {
      console.warn('Failed to clear deferred data:', error);
    }
  }

  // Method to simulate install referrer data for testing
  simulateInstallReferrer(uid: string): void {
    console.log('Simulating install referrer with UID:', uid);
    this.setDeferredData({
      uid,
      source: 'install_referrer',
      timestamp: Date.now()
    });
  }
}

export const deferredDeepLinkService = DeferredDeepLinkService.getInstance();
